import { useResetOnChange } from '@sparklayer-io/spark-dash-ui/hooks/use-reset-on-change';
import { Button, Checkbox, Dropdown, Form, Input } from '@ui/themed';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import type { FormField } from '../types';

type ActionFormProps = {
  schema: FormField[];
  data: Record<string, unknown>;
  errors: Record<string, string[]>;
  saving: boolean;
  submitLabel?: string;
  onSave: (payload: Record<string, unknown>) => void;
};

export function ActionForm({ schema, data, errors, saving, submitLabel, onSave }: ActionFormProps) {
  const [input, setInput] = useState<Record<string, unknown>>(() => initialInput(schema, data));

  useResetOnChange([schema, data], () => {
    setInput(initialInput(schema, data));
  });

  const updateField = useCallback((property: string, value: unknown) => {
    setInput((prev) => ({ ...prev, [property]: value }));
  }, []);

  const conditionSatisfied = useCallback(
    (field: FormField): boolean => {
      const { condition } = field;
      if (condition === null) return true;

      let satisfied: boolean;
      switch (condition.type) {
        case 'any':
          satisfied = Array.isArray(condition.value)
            ? condition.value.includes(input[condition.on])
            : false;
          break;
        case 'equal':
          satisfied = condition.value === input[condition.on];
          break;
        default:
          satisfied = false;
      }

      if (!satisfied && input[field.property] !== null) {
        updateField(field.property, null);
      }

      return satisfied;
    },
    [input, updateField],
  );

  return (
    <Form onSubmit={() => onSave(input)}>
      {schema.map((field) => {
        if (!conditionSatisfied(field)) return null;

        const error = errors[field.property]?.join('\n');

        return (
          <div key={field.property}>
            {field.type === 'checkbox' ? (
              <Checkbox
                name={field.property}
                label={field.label}
                checked={!!input[field.property]}
                error={error}
                onChange={() => updateField(field.property, !input[field.property])}
              />
            ) : field.type === 'select' ? (
              <Dropdown
                id={field.property}
                label={field.label}
                placeholder={t('features:staff.components.action_form.select_placeholder')}
                value={(input[field.property] as string) ?? ''}
                error={error}
                options={
                  field.attributes.options?.map((option) => ({
                    value: option.value,
                    label: option.text,
                  })) ?? []
                }
                onChange={(value) => updateField(field.property, value)}
              />
            ) : (
              <Input
                name={field.property}
                label={field.label}
                description={field.description}
                type={field.type === 'date' ? 'date' : undefined}
                inputMode={field.type === 'number' ? 'numeric' : 'text'}
                value={(input[field.property] as string) ?? ''}
                error={error}
                onChange={(value) => updateField(field.property, value)}
              />
            )}
          </div>
        );
      })}
      <Form.Actions>
        <Button type="submit" loading={saving}>
          {submitLabel ?? t('buttons:save')}
        </Button>
      </Form.Actions>
    </Form>
  );
}

function initialInput(schema: FormField[], data: Record<string, unknown>): Record<string, unknown> {
  const initial: Record<string, unknown> = {};
  for (const field of schema) {
    initial[field.property] = data[field.property] ?? null;
  }
  return initial;
}
